import { AlertsView } from '../view/AlertsView';
import { templateAlerts } from '../view/template';
import { AlertsRepository } from '../repository/AlertsRepository';
import { BaseWebComponentController, WebComponentEvent } from '../../framework/BaseWebComponentController';
import { AlertsWebComponent } from '..';
import {
  InitAlertsRequest,
  UpdateActiveViewResponse,
  ValidateViewNameResponse,
} from '@nirvana/shared/dtos/types/alerts/gz-console';
import { Column, Filter, FiltersHelper } from '@nirvana/shared/dtos/types/shared/filters';
import { FilterEvents, GridEvents } from '../../grid/types/common';
import {
  ColumnDetails as ColumnDetailsAlerts,
  ColumnKeys,
  ColumnValue,
  CreateViewConfiguration,
  ViewConfiguration,
  ViewConfiguration as FrontendViewConfiguration,
  ViewType,
} from '@nirvana/shared/dtos/types/alerts/common';
import {
  GetRawEventsCsvStreamResponse,
  GetRawEventsStreamCsvRequest,
} from '@nirvana/shared/dtos/types/raw-events/gz-console';
import { downloadBlob } from '../../utils/files.utils';
import {
  NirvanaErrorCode,
  StreamMessage,
  StreamMessageType,
  StreamStopReason,
} from '@nirvana/shared/dtos/types/shared/communication';
import { GzToast, ToastSeverity } from '../../ui/gzToastNotification/type';
import { GzToastNotificationWebComponent } from '../../ui/gzToastNotification';
import { getFormattedDateTime, TimestampFormat } from '@nirvana/shared/time';
import { GzButtonState } from '../../ui/gzButton/types';
import { SectionType, SmartViewEvents, SmartViewItemConfiguration } from '../../grid/gridSmartViews/types';
import { SaveViewFormDialogSchema } from '../../grid/uiElements/smartViewSaveDialog/types';
import { RemoteValidator } from '../../ui/gzForm/validators/remote.validator';
import { RequiredValidator } from '../../ui/gzForm/validators/required.validator';
import { SideNavEvents } from '../../ui/gzPushBodyLayout/types';
import { CompanyFilterEvents } from '../../ui/gzCompanyFilter/types/types';
import { EnumListItem } from '../../ui/gzEnumList/types';
import { InitAlertsResponseFrontend, PREDEFINED_FILTERS_KEYS } from '../types';
import { GridTitleConfig } from '../../grid/uiElements/gridViewTitle/types';
import { DetailsPanelEvents } from '../../alertsDetailsPanel/types';
import { CommonThreatsNavigationHelper } from '@nirvana/shared/navigation';
import { ActionTakenEnum } from '@nirvana/shared/dtos/types/shared/threats';

const isEqual = require('lodash.isequal');

export class AlertsController extends BaseWebComponentController {
  public component: AlertsWebComponent;
  public dataRepository: AlertsRepository;
  public view: AlertsView;
  public viewConstructor: typeof AlertsView;
  public dataRepositoryConstructor: typeof AlertsRepository;
  private parts: Array<string> = [];

  constructor(component: AlertsWebComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: AlertsView,
      viewTemplate: templateAlerts,
      dataRepositoryConstructor: AlertsRepository,
    });
  }

  computeEventListeners() {
    return [
      {
        element: this.view.dom.$wrapper,
        event: CompanyFilterEvents.VALUES_UPDATED,
        callback: this.updateCompanyColumnDetails,
      },
      {
        element: this.view.dom.$grid,
        event: GridEvents.EXPORT_CSV,
        callback: this.exportCsv,
      },
      {
        element: this.view.dom.$gridSmartViews,
        event: SmartViewEvents.SMART_VIEW_SELECTED,
        callback: this.handleSmartViewClicked,
      },
      {
        element: this.view.dom.$gridSmartViews,
        event: SmartViewEvents.SMART_VIEW_DELETED,
        callback: this.handleSmartViewDeleted,
      },
      {
        element: this.view.dom.$gridSmartViews,
        event: SmartViewEvents.SMART_VIEW_CREATED,
        callback: this.handleSmartViewCreated,
      },
      {
        element: this.view.dom.$gridSmartViews,
        event: SmartViewEvents.SMART_VIEW_NAME_UPDATED,
        callback: this.updateViewName,
      },
      {
        element: this.view.dom.$grid,
        event: SmartViewEvents.SMART_VIEW_SAVED,
        callback: this.updateViewProps,
      },
      {
        element: this.view.dom.$grid,
        event: SmartViewEvents.SHOW_SAVE_VIEW_AS_DIALOG,
        callback: this.showSaveAsDialog,
      },
      {
        element: this.view.dom.$pushBodyLayout,
        event: SideNavEvents.OPEN,
        callback: this.handleSmartViewsNavOpen,
      },
      {
        element: this.view.dom.$pushBodyLayout,
        event: SideNavEvents.CLOSE,
        callback: this.handleSmartViewsNavClose,
      },
      {
        element: this.view.dom.$grid,
        event: GridEvents.ROW_CLICKED,
        callback: this.handleDetailsPanelOpen,
      },
      {
        element: this.view.dom.$pushBodyLayout,
        event: DetailsPanelEvents.SHOW_ALERTS,
        callback: this.handleShowInAlerts,
      },
      {
        element: this.view.dom.$pushBodyLayout,
        event: SideNavEvents.CLICKED,
        callback: this.handlePushBodyLayoutClicked,
      },
      {
        element: this.view.dom.$grid,
        event: FilterEvents.FILTERS_UPDATED,
        callback: this.handleFiltersUpdatedEvent,
      },
    ];
  }

  async getDataForView(): Promise<InitAlertsResponseFrontend> {
    this.view.setLoadingState();

    try {
      const shouldUseExternallyAppliedParams = this.shouldUseExternallyAppliedParams();
      const initAlertsParams: InitAlertsRequest = {
        locale: this.dependencies.getUserLanguage(),
      };

      const res = await this.dataRepository.initAlerts(initAlertsParams);

      if (!(res.views || []).length) {
        throw new Error('Missing views.');
      }

      let activeViewId = res.activeViewId;

      if (shouldUseExternallyAppliedParams) {
        activeViewId = res.defaultViewProps.id;
      }

      this.component.isPartner = res.isPartner;
      this.component.currentViewId = activeViewId;
      this.component.allColumns = res.allColumns;
      this.component.allDirectManagement = res.allDirectManagement;
      this.component.filterKeys = res.isPartner
        ? PREDEFINED_FILTERS_KEYS
        : PREDEFINED_FILTERS_KEYS.filter(filterKey => filterKey !== ColumnKeys.COMPANY);

      const defaultSelectedTargetIds = FiltersHelper.getCompanyIdsFromView(res.defaultViewProps.filters);
      const defaultViewProps = this.removeCompanyFilterForCustomer([res.defaultViewProps])[0];
      const views = this.removeCompanyFilterForCustomer(res.views);

      this.component.defaultSelectedTargetIds = defaultSelectedTargetIds;
      this.component.defaultViewProps = defaultViewProps;
      if (this.view.dom.$pushBodyLayout.isOpen !== res.viewsPanelOpen) {
        this.view.dom.$pushBodyLayout.toggleSideNav();
      }

      return {
        isPartner: res.isPartner,
        allColumns: res.allColumns,
        activeViewId: activeViewId,
        viewsPanelOpen: res.viewsPanelOpen,
        views: views.map(view => {
          let displayedColumns = view.displayedColumns,
            filters = view.filters;

          if (view.id === res.defaultViewProps.id && shouldUseExternallyAppliedParams) {
            // Concatenate drill down required columns with default view columns
            displayedColumns = Array.from(
              new Set(this.component.externalParams.displayedColumns.concat(view.displayedColumns))
            );
            filters = this.component.externalParams.filters;
          }

          return {
            id: view.id,
            name: view.name,
            type: view.type,
            displayedColumns: displayedColumns,
            filters: filters,
            compactView: view.compactView,
            pageSize: view.pageSize,
            columnConfigs: view.columnConfigs,
          };
        }),
        defaultViewProps: defaultViewProps,
        isDrillDown: shouldUseExternallyAppliedParams,
        filterKeys: this.component.filterKeys,
        defaultSelectedTargetIds: defaultSelectedTargetIds,
        allDirectManagement: res.allDirectManagement,
      };
    } catch (error) {
      this.view.dom.$pushBodyLayout.toggleOpenAndDisabledSideNav();
      throw error;
    } finally {
      this.view.removeLoadingState();
    }
  }

  private shouldUseExternallyAppliedParams() {
    // Handling the MDR Super analyst workflow
    if (!Application.Main.getUser().mdrSuperAnalyst) {
      // Handling the base case
      return !!Object.values(this.component.externalParams || {}).length;
    }

    const activeCompany = Application.Main.getActiveCompany();

    const filters = [
      CommonThreatsNavigationHelper.getLastDetectedOnFilterFromTimeRange(1),
      CommonThreatsNavigationHelper.getCategoryFilter(),
      {
        actionTaken: {
          in: [
            ActionTakenEnum.DELETED,
            ActionTakenEnum.BLOCKED,
            ActionTakenEnum.QUARANTINED,
            ActionTakenEnum.REMEDIATED,
            ActionTakenEnum.MALICIOUS_PROCESS_KILLED,
            ActionTakenEnum.REPLACED_ATTACHMENT,
            ActionTakenEnum.DELETED_ATTACHMENT,
            ActionTakenEnum.REJECTED_EMAIL,
            ActionTakenEnum.UNRESOLVED,
            ActionTakenEnum.REPORTED,
            ActionTakenEnum.ALLOWED,
            ActionTakenEnum.READ_ONLY,
            ActionTakenEnum.DELIVERED,
          ],
        },
      },
      CommonThreatsNavigationHelper.getDeviceNameFilter(),
      { company: { in: [activeCompany.id] } },
    ];

    this.component.externalParams = {
      filters,
      displayedColumns: [],
    };

    return true;
  }

  private async handleFiltersUpdatedEvent(ev: WebComponentEvent) {
    if (!Application.Main.getUser().mdrSuperAnalyst) {
      return;
    }

    const newFilters = ev.detail.updatedFilters || [];
    const companyFilter = newFilters.find(filter => !!filter.company);
    const companyFilterValues = companyFilter.company.in || [];

    // If just a company was selected, sticky it across the console
    if (companyFilterValues.length !== 1) {
      return;
    }

    const companyId = companyFilterValues[0];
    // We also need the company name to sticky it, iterating already loaded companies is the fastest way
    const allLoadedCompanies = this.getAllLoadedCompanies();
    const loadedCompany = allLoadedCompanies.find(columnValue => columnValue.value === companyId);

    if (!loadedCompany) {
      /*
       Should never happen,
       we are handling an event that gave us a company which was just selected in the filter by the user
      */
      throw new Error("User selected a company which isn't properly loaded!");
    }

    Application.Main.setSelectedCompany(loadedCompany.value, loadedCompany.label);
  }

  private async exportCsv(event: WebComponentEvent): Promise<void> {
    const csvTime = getFormattedDateTime(TimestampFormat.FILES, Date.now(), this.dependencies.getTimezone());

    const csvName = `Threats Xplorer ${this.dependencies.getUserLanguage()} ${csvTime}.csv`;

    try {
      this.view.dom.$grid.controller.updateCsvButtonState(GzButtonState.IS_LOADING);

      const filters = this.view.dom.$grid.dataProvider.getFiltersForBackend(
        event.detail.filters,
        this.view.dom.$grid.dataProvider.firstPageAccessedTimestamp
      );

      const requestParams: GetRawEventsStreamCsvRequest = {
        locale: this.dependencies.getUserLanguage(),
        timezone: this.dependencies.getTimezone(),
        filters: filters,
      };

      const { output } = await this.dependencies.api.clients.RawEventsStream.getRawEventsCsv(requestParams);

      output.subscribe(response => this.handleResponse(response, csvName));

      return;
    } catch (error) {
      if (!error.code) {
        this.handleSubscriberError(csvName);
        console.error(error);
        return;
      }

      this.view.dom.$grid.controller.updateCsvButtonState(GzButtonState.ENABLED);

      switch (error.code) {
        case NirvanaErrorCode.FEATURE_CURRENTLY_NOT_AVAILABLE:
          this.buildToastNotification(
            this.dependencies.translateFn('The export functionality is temporarily disabled. Please try again later.'),
            ToastSeverity.INFORMATIVE
          );
          break;
        case NirvanaErrorCode.INVALID_DATA:
          this.buildToastNotification(
            this.dependencies.translateFn('Failed to export. The filters contain invalid data.'),
            ToastSeverity.WARNING
          );
          break;
        default:
          this.handleSubscriberError(csvName);
          console.error(error);
          break;
      }
    }
  }

  private handleResponse(response: StreamMessage.Message, csvName: string) {
    switch (response.type) {
      case StreamMessageType.DATA:
        this.handleStreamData(response as StreamMessage.StreamData<GetRawEventsCsvStreamResponse>, csvName);
        break;
      case StreamMessageType.STREAM_STOP:
        this.handleStreamStop(response as StreamMessage.StreamStop, csvName);
        break;
      default:
        this.handleSubscriberError(csvName);
        break;
    }
  }

  private handleStreamData(response: StreamMessage.StreamData<GetRawEventsCsvStreamResponse>, csvName: string) {
    if (!response.data.csvBytes) {
      this.handleSubscriberError(csvName);
      return;
    }
    this.parts.push(response.data.csvBytes);
  }

  private handleStreamStop(response: StreamMessage.StreamStop, csvName: string) {
    if (response.streamStop.reason !== StreamStopReason.NO_MORE_DATA) {
      this.handleSubscriberError(csvName);
      return;
    }

    this.parts.unshift('\ufeff'); // send UTF-8 header to encode file properly
    const blob = new Blob(this.parts, { type: 'text/csv' });

    downloadBlob(blob, csvName);
    this.parts.length = 0; // reset array
    this.view.dom.$grid.controller.updateCsvButtonState(GzButtonState.ENABLED);

    this.buildToastNotification(
      this.dependencies.translateFn('Successfully exported {fileName}', { fileName: csvName }),
      ToastSeverity.SUCCESS
    );
  }

  private buildToastNotification(message: string, severity: ToastSeverity) {
    const toastNotificationConfigs: GzToast = {
      message,
      hasAction: false,
      severity: severity,
      keepOpen: false,
      autoCloseDelayMs: 10000,
    };

    GzToastNotificationWebComponent.buildToastWrapperElement(toastNotificationConfigs);
  }

  private handleSubscriberError(csvName: string) {
    this.parts.length = 0; // reset csv parts
    this.view.dom.$grid.controller.updateCsvButtonState(GzButtonState.ENABLED);
    this.buildToastNotification(
      this.dependencies.translateFn('Failed to export {fileName}', { fileName: csvName }),
      ToastSeverity.ERROR
    );
  }
  private async handleSmartViewClicked(event: WebComponentEvent) {
    const smartViewConfiguration = event.detail.smartViewConfiguration as SmartViewItemConfiguration;

    const viewDetails: ViewConfiguration = this.transformSmartViewToViewConfiguration(smartViewConfiguration);

    this.component.currentViewId = smartViewConfiguration.id;
    this.view.dom.$gridSmartViews.activeViewId = smartViewConfiguration.id;

    let { activeViewProps } = await this.setActiveViewId(smartViewConfiguration.id);

    if (!isEqual(activeViewProps, viewDetails)) {
      // Targets changed, view was cleaned
      const smartViewConfigurationCleaned = this.transformViewConfigurationToSmartView(activeViewProps);
      await this.view.dom.$gridSmartViews.controller.updateViewPropsFromSection(
        smartViewConfigurationCleaned,
        SectionType.SAVED_VIEWS
      );
    }

    activeViewProps = this.removeCompanyFilterForCustomer([activeViewProps])[0];

    this.view.buildGridView(
      [activeViewProps],
      this.component.currentViewId,
      this.component.allColumns,
      this.component.defaultViewProps,
      false,
      this.component.isPartner,
      this.component.filterKeys,
      this.component.defaultSelectedTargetIds,
      this.component.allDirectManagement
    );
  }

  private async setActiveViewId(activeViewId: string): Promise<UpdateActiveViewResponse> {
    return await this.dataRepository.updateActiveView({
      activeViewId: activeViewId,
      locale: Application.Main.getLanguage(),
    });
  }
  private async handleSmartViewDeleted(event: WebComponentEvent) {
    try {
      await this.dataRepository.deleteView({ viewId: event.detail.viewId });

      if (event.detail.viewId === this.component.currentViewId) {
        // reset active view to default, also build grid for default
        this.component.currentViewId = this.component.defaultViewProps.id;
        this.view.dom.$gridSmartViews.activeViewId = this.component.defaultViewProps.id;
        await this.setActiveViewId(this.component.defaultViewProps.id);
        await this.render();
      } else {
        await this.view.dom.$gridSmartViews.controller.removeViewFromSection(event.detail.viewId);
      }

      this.buildToastNotification(
        this.dependencies.translateFn('Successfully deleted {viewName}', { viewName: event.detail.viewName }),
        ToastSeverity.SUCCESS
      );
    } catch (error) {
      this.buildToastNotification(
        this.dependencies.translateFn('Failed to delete {viewName}', { viewName: event.detail.viewName }),
        ToastSeverity.ERROR
      );
    }
  }
  private async handleSmartViewCreated(event: WebComponentEvent) {
    const viewConfiguration: CreateViewConfiguration = {
      name: event.detail.viewName.trimEnd().trimStart(),
      isShared: event.detail.isShared,
      type: ViewType.CUSTOM,
      filters: this.view.dom.$grid.filters,
      displayedColumns: this.view.dom.$grid.columns,
      compactView: this.view.dom.$grid.isCompactView,
      columnConfigs: this.view.dom.$grid.activeViewProps.columnConfigs,
      pageSize: this.view.dom.$grid.pageSize,
    };

    try {
      viewConfiguration.filters = this.addCompanyFilterToCustomers(viewConfiguration.filters);

      const response = await this.dataRepository.createView({ view: viewConfiguration });

      const smartViewItemConfiguration: SmartViewItemConfiguration = this.transformViewConfigurationToSmartView(
        response.view
      );

      this.component.currentViewId = smartViewItemConfiguration.id;
      this.view.dom.$gridSmartViews.activeViewId = smartViewItemConfiguration.id;
      await this.setActiveViewId(smartViewItemConfiguration.id);

      await this.view.dom.$gridSmartViews.controller.addViewToSection(
        smartViewItemConfiguration,
        SectionType.SAVED_VIEWS
      );

      const activeViewProps = this.removeCompanyFilterForCustomer([response.view])[0];

      this.view.dom.$grid.activeViewProps = activeViewProps;
      this.view.dom.$grid.activeViewInitialProps = { ...activeViewProps };
      this.view.dom.$grid.controller.disableSmartViewActions();
      this.view.dom.$grid.titleConfig = new GridTitleConfig(smartViewItemConfiguration.viewName);

      this.buildToastNotification(
        this.dependencies.translateFn('Successfully created {viewName}', { viewName: viewConfiguration.name }),
        ToastSeverity.SUCCESS
      );
    } catch (error) {
      this.buildToastNotification(
        this.dependencies.translateFn('Failed to create {viewName}', { viewName: viewConfiguration.name }),
        ToastSeverity.ERROR
      );
    }
  }

  private appendCompaniesData(
    allColumnDetails: Array<ColumnDetailsAlerts>,
    params: {
      filterOptions: EnumListItem[];
      defaultValue: Array<string>;
    }
  ) {
    return allColumnDetails.map(columnDetails => {
      if (!(columnDetails.column === Column.COMPANY)) {
        return columnDetails;
      }

      return {
        ...columnDetails,
        values: params.filterOptions,
        defaultTarget: params.defaultValue,
      };
    });
  }

  private getAllLoadedCompanies(): ColumnValue[] {
    const companyColumnDetails = (this.view.dom.$grid.allColumnDetails as Array<ColumnDetailsAlerts>).find(
      columnDetails => columnDetails.column === Column.COMPANY
    );
    return companyColumnDetails?.values || [];
  }

  private updateCompanyColumnDetails(ev: WebComponentEvent) {
    this.view.dom.$grid.allColumnDetails = this.appendCompaniesData(this.view.dom.$grid.allColumnDetails, ev.detail);
  }

  private transformSmartViewToViewConfiguration(smartViewConfiguration: SmartViewItemConfiguration): ViewConfiguration {
    return {
      id: smartViewConfiguration.id,
      name: smartViewConfiguration.viewName,
      compactView: smartViewConfiguration.isCompactView,
      columnConfigs: smartViewConfiguration.columnConfigs,
      displayedColumns: smartViewConfiguration.displayedColumns,
      filters: smartViewConfiguration.filters,
      pageSize: smartViewConfiguration.pageSize,
      type: smartViewConfiguration.viewType,
    };
  }

  private transformViewConfigurationToSmartView(viewConfiguration: ViewConfiguration): SmartViewItemConfiguration {
    return {
      displayedColumns: viewConfiguration.displayedColumns,
      filters: viewConfiguration.filters,
      id: viewConfiguration.id,
      isCompactView: viewConfiguration.compactView,
      pageSize: viewConfiguration.pageSize,
      viewName: viewConfiguration.name,
      viewType: viewConfiguration.type,
      columnConfigs: viewConfiguration.columnConfigs,
    };
  }

  private async updateViewProps(event: WebComponentEvent) {
    try {
      const viewConfiguration = event.detail.viewConfiguration;
      viewConfiguration.filters = this.addCompanyFilterToCustomers(viewConfiguration.filters);

      await this.dataRepository.updateViewProps({ viewConfiguration: viewConfiguration });
      await this.view.dom.$gridSmartViews.controller.updateViewPropsFromSection(
        this.transformViewConfigurationToSmartView(event.detail.viewConfiguration),
        SectionType.SAVED_VIEWS,
        true
      );
      this.view.dom.$grid.activeViewProps = viewConfiguration;
      this.view.dom.$grid.activeViewInitialProps = { ...viewConfiguration };
      this.view.dom.$grid.controller.disableSmartViewActions();
      this.view.dom.$grid.columnConfigs = event.detail.viewConfiguration.columnConfigs;
    } catch (e) {
      this.buildToastNotification(
        this.dependencies.translateFn('Failed to save {viewName}', { viewName: event.detail.viewConfiguration.name }),
        ToastSeverity.ERROR
      );
    }
  }

  private showSaveAsDialog() {
    const { smartViewsStrings } = this.view.getTranslations();
    const config: SaveViewFormDialogSchema = {
      shouldDisplayCheckbox: false,
      dialogText: smartViewsStrings.saveViewFormDialog.dialogText,
      nameFieldLabel: smartViewsStrings.saveViewFormDialog.nameFieldLabel,
      nameInputErrorText: smartViewsStrings.saveViewFormDialog.nameInputErrorText,
      nameInputPlaceholder: smartViewsStrings.saveViewFormDialog.nameInputPlaceholder,
      headline: smartViewsStrings.saveViewFormDialog.headline,
      saveButtonLabel: smartViewsStrings.actions.save,
      cancelButtonLabel: smartViewsStrings.actions.cancel,
      nameInputValidators: [
        RequiredValidator('This field is required.'),
        RemoteValidator(
          (value: string) => {
            const viewName = value.trimEnd().trimStart();
            return this.dependencies.api.clients.Alerts.validateViewName({ viewName: viewName });
          },
          (response: ValidateViewNameResponse) => response.valid,
          this.dependencies.translateFn('A view with this name already exists.'),
          this.dependencies.translateFn('A communication error has occurred. Please try again later.')
        ),
      ],
    };
    this.view.dom.$gridSmartViews.showSaveViewDialog(config);
  }

  private async updateViewName(event: WebComponentEvent) {
    const smartViewConfig: SmartViewItemConfiguration = event.detail.smartViewItemConfiguration;
    smartViewConfig.viewName = event.detail.updatedViewName;

    const viewConfiguration = this.transformSmartViewToViewConfiguration(smartViewConfig);
    viewConfiguration.filters = this.addCompanyFilterToCustomers(viewConfiguration.filters);

    const updatedView = await this.dataRepository.updateViewProps({
      viewConfiguration: viewConfiguration,
    });
    const smartUpdatedView = this.transformViewConfigurationToSmartView(updatedView.view);

    this.view.dom.$grid.titleConfig = new GridTitleConfig(smartUpdatedView.viewName);
  }

  private async handleSmartViewsNavOpen() {
    await this.dataRepository.updateSmartViewsState({
      viewsPanelOpen: true,
    });
  }

  private async handleSmartViewsNavClose() {
    await this.dataRepository.updateSmartViewsState({
      viewsPanelOpen: false,
    });
  }

  private removeCompanyFilterForCustomer(
    viewConfigs: Array<FrontendViewConfiguration>
  ): Array<FrontendViewConfiguration> {
    if (this.component.isPartner) {
      return viewConfigs;
    }

    return viewConfigs.map(viewConfig => {
      viewConfig.filters = viewConfig.filters.filter(filter => !filter.company);

      return viewConfig;
    });
  }

  private addCompanyFilterToCustomers(filters: Array<Filter>): Array<Filter> {
    if (this.component.isPartner) {
      return filters;
    }

    if (filters.find((filter: Filter) => filter.company)) {
      return filters;
    }

    const companyFilter = {
      company: {
        in: this.component.defaultSelectedTargetIds,
      },
    };

    return filters.concat(companyFilter);
  }

  private async handleDetailsPanelOpen(event) {
    const item = event.detail.rowDetails;
    if (!item || !item._id) {
      return;
    }

    const alertsDetailsPanel = this.view.buildDetailsPanel(
      item._id,
      this.component.allColumns,
      this.component.isPartner
    );
    this.view.dom.$detailsPanel.setMenuContent(alertsDetailsPanel);
    this.view.dom.$detailsPanel.openSideMenu();
  }

  private handleShowInAlerts(e: CustomEvent) {
    const filters = this.view.dom.$grid.filters;
    const companyFilter = filters.find(filter => filter.company !== undefined);
    const defaultViewProps = this.component.defaultViewProps;

    const newFilters = defaultViewProps.filters.map(filter => {
      if (filter.deviceName !== undefined) {
        return {
          deviceName: {
            startsWith: e.detail.endpointName,
          },
        };
      }
      if (filter.company?.in !== undefined) {
        return companyFilter;
      }

      return filter;
    });

    const newViewProps = { ...defaultViewProps, filters: newFilters };

    this.view.dom.$gridSmartViews.propertiesSet = false;
    this.view.dom.$gridSmartViews.activeViewId = defaultViewProps.id;
    this.view.dom.$gridSmartViews.propertiesSet = true;

    this.view.dom.$detailsPanel.propertiesSet = false;
    this.view.dom.$detailsPanel.closeSideMenu();
    this.view.dom.$detailsPanel.propertiesSet = true;

    this.view.buildGridView(
      [newViewProps],
      this.component.currentViewId,
      this.component.allColumns,
      this.component.defaultViewProps,
      true,
      this.component.isPartner,
      this.component.filterKeys,
      this.component.defaultSelectedTargetIds,
      this.component.allDirectManagement
    );
  }

  private handlePushBodyLayoutClicked() {
    if (this.view.dom.$detailsPanel.isOpen) {
      this.view.dom.$detailsPanel.closeSideMenu();
    }

    this.view.dom.$grid.closeSettingsSideMenu();
  }
}
